import * as R from 'ramda';

import type { MergedTheme, ResolvedTheme, ThemeDef } from '@typings/theme.ts';
import { resolveSelfRef } from '@utils/configUtils.ts';
import { rgba } from '@utils/dom.ts';
import modDefaultTheme from '@utils/modDefaultTheme.tsx';
import molDefaultTheme from '@utils/molDesktopDefaultTheme.tsx';

// Self-hosted Roboto (weights match theme.fontWeights: 400/500/700). Imported as a
// side effect so Vite bundles the woff2 subsets and serves them same-origin; each
// weight's CSS uses unicode-range, so the browser only fetches the subsets a given
// venue's text actually needs. Self-hosting keeps the rendered font deterministic
// across devices/CI/visual regression tooling instead of depending on a Google Fonts fetch landing.
import '@fontsource/roboto/400.css';
import '@fontsource/roboto/500.css';
import '@fontsource/roboto/700.css';

const defaultPalette = {
  primary: '#2266DD',
  primaryButton: '#2266DD',
  primaryButtonText: '#FFFFFF',
  primaryButtonHover: '#124CB1',
  secondary: '#333333',
  secondaryButton: '#EBEBEB',
  secondaryButtonText: '#2266DD',
  secondaryButtonHover: '#D9D9D9',
  errorState: '#E52727',
  primaryText: '#333333',
  secondaryText: '#666666',
  background: '#FFFFFF',
  hoverClickStateBase: '>>primaryText',
  focusOutlineColor: '#c76d00',

  toolTipBackground: '#000',
  toolTipText: '#FFFFFF',

  mapNavBackground: '#333333',
  mapNavText: '#FFFFFF',
  mapNavSearchResults: '#CA4800',
  mapNavSearchResultsText: '#FFFFFF',

  widgetBackground: '#F9F9F9',
  widgetIconFill: '#333333',
  widgetText: '#333333',

  statusOpenBackground: '#1F8845',
  statusOpenText: '#FFFFFF',
  statusClosedBackground: '#ebebeb',
  statusClosedText: '#333333',
  statusEarlyBackground: '#057DBC',
  statusEarlyText: '#FFFFFF',
  statusDelayedBackground: '#CA4800',
  statusDelayedText: '#FFFFFF',
  statusCancelledBackground: '#E52727',
  statusCancelledText: '#FFFFFF',
  statusSuccessBackground: '#229653',
  statusSuccessText: '#FFFFFF',
  statusErrorBackground: '#1F8845',
  statusErrorText: '#FFFFFF',

  adminMarkerText: '#4d4563',
  adminMarkerStroke: '#FFFFFF',
  programmaticPinMarkersText: '#4d4563',
  programmaticPinMarkersStroke: '#FFFFFF',

  keyboard: {
    get background() {
      return '#D2D5DA';
    },
    get buttonBackground() {
      return '#FFFFFF';
    },
    get buttonPrimary() {
      return '#6D6D6D';
    },
    get actionButtonBackground() {
      return '#D0D0D0';
    },
    get actionButtonColor() {
      return '#6D6D6D';
    },
    get submitButtonBackground() {
      return '#A2A2A2';
    },
    get submitButtonColor() {
      return '#FFFFFF';
    },
    get activeButtonBackground() {
      return '#AEB4BD';
    },
    get activeButtonColor() {
      return '#FFFFFF';
    },
  },
};

const defaultFonts = {
  family: `'Roboto','Helvetica','Arial',sans-serif`,
  size: {
    h1: '24px',
    h2: '18px',
    h3: '16px',
    h4: '14px',
    h5: '12px',
    h6: '10px',
  },
};

const defaultHeader = {
  padding: 'min(20px, 2vw)',
  paddingMobile: '12px 20px',
  paddingWithLogo: 'min(20px, 2vw)',
  paddingWithLogoMobile: '12px 18px 17px 20px',
  hasLogo: true,
};

const defaultLogo = {
  height: '28px',
  padding: '0',
  margin: '0',
  centered: false,
};

const fontWeights = {
  lighter: 400,
  normal: 500,
  bold: 700,
};

const defaultTheme = {
  colors: defaultPalette,
  fonts: defaultFonts,
  fontWeights,
  header: defaultHeader,
  logo: defaultLogo,
  globalRadius: 4,
  standardRadius: '8px',
  // The above globalRadius value has been exposed to customers for theming, but we have not used it consistenly
  // enough to match current Figma spec. So standardRadius has been introduced and used to implement
  // current design spec where all border radii match. Going forward, use standardRadius.
};

/* eslint-disable @typescript-eslint/no-explicit-any -- mergeDeepRight returns untyped; full theme shape is inferred at runtime */

const getTheme = (customTheme: ThemeDef, defaultThemeName?: string): ResolvedTheme => {
  let DStheme: any = null;
  if (defaultThemeName === 'mod') {
    DStheme = modDefaultTheme;
  } else {
    DStheme = molDefaultTheme;
  }
  const defaultDStheme = R.mergeDeepRight(defaultTheme, DStheme); // merge default theme with Design Systems theme
  const theme = R.mergeDeepRight(defaultDStheme, customTheme) as MergedTheme; // apply client-specific theme values

  const colors: Record<string, string> = Object.assign({}, theme.colors);
  const header: Record<string, string> = Object.assign({}, theme.header) as Record<string, string>;
  const logo: Record<string, string | number> = Object.assign({}, theme.logo) as Record<string, string | number>;
  if (logo.height) {
    logo.height = parseInt(logo.height as string);
  } // convert strings like "28px" to numbers, which is what the Logo component expects

  theme.logo = logo;

  colors.navBarButtonBackground = colors.navBarButtonBackground || colors.primaryButtonText;
  colors.navBarButtonText = colors.navBarButtonText || colors.primaryButton;
  colors.categoryBadgeWayfindingTrain ??= colors.categoryBadgeWayfindingTransit;
  colors.categoryBadgeWayfindingBus ??= colors.categoryBadgeWayfindingTransit;

  theme.colors = resolveSelfRef(colors);

  theme.colors.hoverClickState = theme.colors.hoverClickState || rgba(theme.colors.hoverClickStateBase, 0.1);

  header.paddingWithLogo = header.paddingWithLogo || header.padding;
  header.paddingWithLogoMobile = header.paddingWithLogoMobile || header.paddingMobile;
  theme.header = header;

  return {
    ...theme,
    borderRadius: (radius: number | string) => `border-radius: ${radius}px; background-clip: padding-box;`,
    boxShadow: (horiz = 0, vert = 0, blur = '8px', spread = '0px', color = 'rgba(0, 0, 0, 0.25)') =>
      `box-shadow: ${horiz} ${vert} ${blur} ${spread} ${color};`,
    fontSize: (size: number | string) =>
      `font-size: ${typeof size === 'string' && size.startsWith('h') ? theme.fonts.size[size] : size};`,
  } as ResolvedTheme;
};
/* eslint-enable @typescript-eslint/no-explicit-any */

export { getTheme };
